/**
 * ProductDetailPage — a dedicated landing page for the five YADA products
 * that used to be listed on /products without a page of their own:
 *
 *   hackathon        → 生涯與升學 Hackathon
 *   explorationTrip  → 探索之旅
 *   mentorship       → 師友及教練服務
 *   flashCards       → 生涯與升學 Flash Cards
 *   clawMachine      → 夾公仔機
 *
 * WHERE THE COPY COMES FROM
 * -------------------------
 * Two sources, deliberately kept apart:
 *
 *   1. The CMS (`cms_products`) owns the product's IDENTITY — its title,
 *      badge, one-line introduction, accent gradient and icon. The row is
 *      localised per locale, so `localizeProducts()` gives us the right
 *      language for the active locale. The CMS `description` is THE one
 *      product introduction on the page: it is rendered once, as the hero
 *      lede, and is not paraphrased anywhere else.
 *
 *   2. i18n (`productDetail.<name>.*`) owns the LONG-FORM copy — the
 *      feature cards, the process steps, the audience list, the FAQ and the
 *      CTA. The translation files still hold it, because they are what
 *      define its shape; an operator can now EDIT it from the CMS admin
 *      (see below) without a deploy.
 *
 * THE COPY IS READ THROUGH THE SLOT RESOLVER
 * ------------------------------------------
 * Every block of copy below is a CMS slot declared in
 * `src/admin/schemas/productDetail.ts`, read through `useSlotObject` /
 * `useSlotT` for THIS product's page slug. The i18n bundle still defines
 * the SHAPE — how many cards, how many FAQ entries — and an operator's
 * override is merged onto it, so editing the copy can never change the
 * layout.
 *
 * The keys are built from the product slug rather than written out five
 * times: `scripts/lint-slots.ts` proves every DECLARED slot key exists in
 * the translation files by importing the schema module (which declares the
 * same six blocks, plus the three SEO strings, for each of the five
 * products), and `scripts/lint-slot-defaults.ts` proves each of them
 * resolves to exactly what i18next returns, in every locale.
 *
 * DEFENSIVE RENDERING
 * -------------------
 * The CMS row may be missing (a product unpublished mid-deploy) and the
 * i18n copy may not exist yet (this page ships before the translation
 * files do). Neither may crash the page: a missing CMS row falls back to
 * the SEO title, and a missing i18n string renders as empty rather than as
 * a dotted key path.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSlotObject, useSlotT } from '@/cms/useSlotT'
import { Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { Container, Card, PageHeader, Reveal } from '@/components/ui'
import { H2, H3 } from '@/components/ui/typography'
import { SITE } from '@/content/site'
import { cmsProducts } from '@/content/cms/products.generated'
import { localizeProducts } from '@/content/cms/productLocale'
import { buildLocalePath, type Locale } from '@/utils/localePath'
import SEO from '@/utils/seo'
import { SITE_URL } from '@/utils/siteMeta'
import {
  buildBreadcrumbList,
  buildFAQPage,
  buildProductWithOffers,
} from '@/utils/structuredData'

/** The five products this page serves. */
export type ProductDetailSlug =
  | 'hackathon'
  | 'explorationTrip'
  | 'mentorship'
  | 'flashCards'
  | 'clawMachine'

/**
 * The public path of every product detail page.
 *
 * Exported so the route registration is data-driven: the router maps each
 * entry to a `<ProductDetailPage slug={…} />` route instead of hard-coding
 * five near-identical route elements.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const PRODUCT_DETAIL_SLUGS: { slug: ProductDetailSlug; path: string }[] = [
  { slug: 'hackathon', path: '/products/hackathon' },
  { slug: 'explorationTrip', path: '/products/exploration-trip' },
  { slug: 'mentorship', path: '/products/mentorship' },
  { slug: 'flashCards', path: '/products/flash-cards' },
  { slug: 'clawMachine', path: '/products/claw-machine' },
]

/**
 * The CMS slug of each product detail page.
 *
 * Three of the five differ from the component's slug: the CMS spells them
 * in kebab-case (`exploration-trip`, `flash-cards`, `claw-machine`), while
 * the component's slug is camelCase so it reads as a JS identifier.
 */
const CMS_SLUG_BY_DETAIL: Record<ProductDetailSlug, string> = {
  hackathon: 'hackathon',
  explorationTrip: 'exploration-trip',
  mentorship: 'mentorship',
  flashCards: 'flash-cards',
  clawMachine: 'claw-machine',
}

/** One `{ title, text }` feature card. */
interface DetailItem {
  title: string
  text: string
}

/** One FAQ question and its answer. */
interface DetailFaqItem {
  q: string
  a: string
}

/** The long-form copy for one product. */
interface DetailCopy {
  chips: string[]
  includes: { tag: string; title: string; items: DetailItem[] }
  process: { tag: string; title: string; items: DetailItem[] }
  audience: { tag: string; title: string; text: string; items: string[] }
  faq: { tag: string; title: string; items: DetailFaqItem[] }
  cta: { title: string; text: string }
}

/** The per-locale SEO strings for one product. */
interface DetailSeo {
  title: string
  description: string
  keywords: string
}

/**
 * Coerce a slot value into a display string.
 *
 * i18next returns the KEY ITSELF when a translation is missing, so a
 * missing key would otherwise print `productDetail.hackathon.includes.tag`
 * to the visitor. Only the two namespaces this page reads are treated as
 * "missing" — a legitimate translation never starts with `productDetail.`
 * or `seo.`, so no real copy is ever swallowed.
 *
 * A CMS OVERRIDE IS NEVER SWALLOWED
 * ---------------------------------
 * The override is stored under the slot key, but the resolver hands back
 * the MERGED VALUE — the operator's own text, not the key — so an override
 * can never look like a missing key. The prefix test below only ever fires
 * on i18next's own "key not found" answer.
 */
function asText(value: unknown): string {
  if (typeof value !== 'string') return ''
  const trimmed = value.trim()
  if (!trimmed) return ''
  if (trimmed.startsWith('productDetail.') || trimmed.startsWith('seo.')) return ''
  return value
}

/**
 * Coerce a slot value into a record.
 *
 * A block's default is an object, but the resolver hands back whatever
 * i18next found for the key — and a key that is missing from the bundle
 * comes back as the key PATH, which is a string. Reading a property off
 * that yields `undefined` anyway; this makes it explicit and keeps the
 * page from throwing if a block ever disappears from the bundles.
 */
function asRecord(value: unknown): Record<string, unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return {}
  return value as Record<string, unknown>
}

/** Coerce an i18n value into a list of strings (empty when missing). */
function asTextArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === 'string')
}

/** Coerce an i18n value into a list of `{ title, text }` cards. */
function asItemArray(value: unknown): DetailItem[] {
  if (!Array.isArray(value)) return []
  return value.map((item) => {
    const record = (item ?? {}) as { title?: unknown; text?: unknown }
    return {
      title: asText(record.title),
      text: asText(record.text),
    }
  })
}

/** Coerce an i18n value into a list of `{ q, a }` FAQ entries. */
function asFaqArray(value: unknown): DetailFaqItem[] {
  if (!Array.isArray(value)) return []
  return value.map((item) => {
    const record = (item ?? {}) as { q?: unknown; a?: unknown }
    return {
      q: asText(record.q),
      a: asText(record.a),
    }
  })
}

export default function ProductDetailPage({ slug }: { slug: ProductDetailSlug }) {
  const { t, i18n } = useTranslation()
  // Internal links carry the active locale prefix — a bare `/contact`
  // would be bounced by the bare-path redirect map into the default locale
  // and drop a visitor who is reading English.
  const locale = (i18n.language || 'zh-Hant') as Locale
  const [openFaq, setOpenFaq] = useState<number | null>(0)

  /**
   * The CMS page that owns this product's copy.
   *
   * Five pages, five slugs — one `cms_pages` row and one admin panel each.
   * The slug is what the CMS stores an override under, so it is what the
   * resolver has to be given for the operator's copy to reach this page.
   */
  const cmsSlug = CMS_SLUG_BY_DETAIL[slug]

  /**
   * The slot resolvers.
   *
   * `slotObj` returns a block's MERGED value — the i18n default with the
   * operator's override merged onto it — never the key path, so a value
   * read from it is a real string whether or not the copy has been
   * overridden. `slotT` is the scalar counterpart, for the SEO strings.
   */
  const slotObj = useSlotObject(cmsSlug)
  const slotT = useSlotT(cmsSlug)

  /**
   * Read the long-form copy for one product.
   *
   * Each block is one json slot: the i18n bundle defines its shape (three
   * cards, two FAQ entries) and the CMS override supplies values only, so
   * the number of rendered cards cannot change.
   */
  const readCopy = (product: ProductDetailSlug): DetailCopy => {
    const root = `productDetail.${product}`
    const includes = asRecord(slotObj(`${root}.includes`))
    const process = asRecord(slotObj(`${root}.process`))
    const audience = asRecord(slotObj(`${root}.audience`))
    const faq = asRecord(slotObj(`${root}.faq`))
    const cta = asRecord(slotObj(`${root}.cta`))
    return {
      chips: asTextArray(slotObj(`${root}.chips`)),
      includes: {
        tag: asText(includes.tag),
        title: asText(includes.title),
        items: asItemArray(includes.items),
      },
      process: {
        tag: asText(process.tag),
        title: asText(process.title),
        items: asItemArray(process.items),
      },
      audience: {
        tag: asText(audience.tag),
        title: asText(audience.title),
        text: asText(audience.text),
        items: asTextArray(audience.items),
      },
      faq: {
        tag: asText(faq.tag),
        title: asText(faq.title),
        items: asFaqArray(faq.items),
      },
      cta: {
        title: asText(cta.title),
        text: asText(cta.text),
      },
    }
  }

  /**
   * Read the per-locale SEO strings for one product.
   *
   * Scalar slots: the SEO component takes the title, the description and
   * the keywords one at a time. `slotT` hands back the operator's override
   * when there is one and the i18n string otherwise.
   */
  const readSeo = (product: ProductDetailSlug): DetailSeo => ({
    title: asText(slotT(`seo.${product}Title`)),
    description: asText(slotT(`seo.${product}Description`)),
    keywords: asText(slotT(`seo.${product}Keywords`)),
  })

  const copy = readCopy(slug)
  const seo = readSeo(slug)

  // The CMS row carries the product's identity, already localised for the
  // active locale. A missing row is survivable: the page falls back to the
  // SEO title and renders the i18n copy on its own.
  const cmsProduct = localizeProducts(cmsProducts, locale).find(
    (product) => product.slug === CMS_SLUG_BY_DETAIL[slug],
  )

  const detailPath =
    PRODUCT_DETAIL_SLUGS.find((entry) => entry.slug === slug)?.path ??
    `/products/${CMS_SLUG_BY_DETAIL[slug]}`

  const productTitle = cmsProduct?.title || cmsProduct?.short_title || seo.title
  const productDescription = cmsProduct?.description ?? ''

  const faqItems = copy.faq.items
  const offerName = copy.cta.title || asText(t('common.getInTouch'))

  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        jsonLd={[
          buildBreadcrumbList([
            { name: asText(t('nav.home')), path: '/' },
            { name: asText(t('nav.products')), path: '/products' },
            { name: productTitle, path: detailPath },
          ]),
          buildProductWithOffers({
            path: detailPath,
            name: productTitle,
            description: productDescription,
            brand: SITE.brand.full,
            keywords: seo.keywords
              .split(',')
              .map((keyword) => keyword.trim())
              .filter(Boolean),
            inLanguage: ['zh-Hant', 'zh-Hans', 'en'],
            // Pricing is on request for every one of these products, so the
            // offer carries NO price: a fabricated `price: "0"` would be a
            // false claim in the structured data.
            offers: [
              {
                name: offerName,
                description: copy.cta.text,
                url: `${SITE_URL}${detailPath}/`,
              },
            ],
          }),
          ...(faqItems.length > 0
            ? [
                buildFAQPage(
                  faqItems.map((item) => ({ question: item.q, answer: item.a })),
                ),
              ]
            : []),
        ]}
      />

      <PageHeader
        tag={cmsProduct?.badge || asText(t('nav.products'))}
        title={productTitle}
        description={productDescription}
      />

      {/* Hero chips — the product's headline features as small pills, plus
          the CMS icon in its own accent gradient. */}
      <section className="bg-surface border-b border-border" aria-labelledby="page-title">
        <Container>
          <div className="flex flex-wrap items-center gap-4">
            {cmsProduct && (
              <div
                className={`w-14 h-14 rounded-yada-xl bg-gradient-to-br ${cmsProduct.accent_gradient} flex items-center justify-center shadow-yada flex-shrink-0`}
              >
                <Icon
                  icon={cmsProduct.icon}
                  className="text-surface"
                  style={{ fontSize: '1.75rem' }}
                  aria-hidden="true"
                />
              </div>
            )}
            <ul className="flex flex-wrap gap-2">
              {copy.chips.map((chip, idx) => (
                <li
                  key={`${idx}-${chip}`}
                  className="inline-flex items-center gap-1.5 bg-surface-2 border border-border text-ink-soft px-3 py-1 rounded-yada-full text-sm font-semibold"
                >
                  <Icon icon="lucide:check" className="text-primary text-xs" aria-hidden="true" />
                  {chip}
                </li>
              ))}
            </ul>
          </div>
        </Container>
      </section>

      {/* 一、方案包括 */}
      <section
        className="py-section-y px-section-x bg-surface-2"
        aria-labelledby="pd-includes-title"
      >
        <Container>
          <Reveal>
            <div className="text-center mb-8">
              <span className="section-title__tagline inline-block mb-3">{copy.includes.tag}</span>
              <H2 id="pd-includes-title" className="text-3xl md:text-4xl text-ink">
                {copy.includes.title}
              </H2>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-gap-cards">
            {copy.includes.items.map((item, idx) => (
              <Reveal key={`${idx}-${item.title}`} delay={idx * 0.1}>
                <Card hover bordered className="h-full">
                  <span
                    aria-hidden="true"
                    className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mb-4"
                  >
                    <Icon icon="lucide:check" className="text-primary text-sm" />
                  </span>
                  <H3 className="mb-2 text-ink">{item.title}</H3>
                  <p className="text-ink-soft leading-relaxed">{item.text}</p>
                </Card>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* 二、服務流程 */}
      <section
        className="py-section-y px-section-x bg-surface"
        aria-labelledby="pd-process-title"
      >
        <Container>
          <Reveal>
            <div className="text-center mb-8">
              <span className="section-title__tagline inline-block mb-3">{copy.process.tag}</span>
              <H2 id="pd-process-title" className="text-3xl md:text-4xl text-ink">
                {copy.process.title}
              </H2>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-gap-cards">
            {copy.process.items.map((item, idx) => (
              <Reveal key={`${idx}-${item.title}`} delay={idx * 0.1}>
                <Card hover bordered className="h-full">
                  <div className="flex items-baseline gap-2 mb-3">
                    <span aria-hidden="true" className="text-sm font-bold text-accent">
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                    <H3 className="text-ink">{item.title}</H3>
                  </div>
                  <p className="text-ink-soft leading-relaxed">{item.text}</p>
                </Card>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* 三、適合對象 */}
      <section
        className="py-section-y px-section-x bg-surface-2"
        aria-labelledby="pd-audience-title"
      >
        <Container size="lg">
          <Reveal>
            <div className="text-center mb-8">
              <span className="section-title__tagline inline-block mb-3">{copy.audience.tag}</span>
              <H2 id="pd-audience-title" className="text-3xl md:text-4xl text-ink">
                {copy.audience.title}
              </H2>
            </div>
          </Reveal>

          {copy.audience.text && (
            <Reveal delay={0.1}>
              <p className="text-ink-soft leading-relaxed text-center max-w-3xl mx-auto mb-8">
                {copy.audience.text}
              </p>
            </Reveal>
          )}

          <Reveal delay={0.15}>
            <ul className="max-w-3xl mx-auto space-y-3">
              {copy.audience.items.map((item, idx) => (
                <li
                  key={`${idx}-${item}`}
                  className="flex items-start gap-3 text-ink-soft leading-relaxed bg-surface rounded-yada-lg border border-border p-4"
                >
                  <span
                    aria-hidden="true"
                    className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5"
                  >
                    <Icon icon="lucide:check" className="text-primary text-xs" />
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </Container>
      </section>

      {/* 四、常見問題 */}
      {faqItems.length > 0 && (
        <section
          className="py-section-y px-section-x bg-surface"
          aria-labelledby="pd-faq-title"
        >
          <Container size="lg">
            <div className="text-center mb-8">
              <span className="section-title__tagline inline-block mb-3">{copy.faq.tag}</span>
              <H2 id="pd-faq-title" className="text-3xl md:text-4xl text-ink">
                {copy.faq.title}
              </H2>
            </div>

            <div className="space-y-3 max-w-3xl mx-auto">
              {faqItems.map((item, idx) => (
                <Reveal key={`${idx}-${item.q}`} delay={idx * 0.05}>
                  <div className="bg-surface-2 rounded-yada-lg border border-border overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                      aria-expanded={openFaq === idx}
                      aria-controls={`faq-${idx}`}
                      className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-surface-3 transition-colors focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
                    >
                      <span className="font-bold text-ink">{item.q}</span>
                      <Icon
                        icon={openFaq === idx ? 'lucide:chevron-up' : 'lucide:chevron-down'}
                        className="text-primary flex-shrink-0"
                        aria-hidden="true"
                      />
                    </button>
                    {openFaq === idx && (
                      <div id={`faq-${idx}`} className="px-5 pb-5 text-ink-soft leading-relaxed">
                        {item.a}
                      </div>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          </Container>
        </section>
      )}

      {/* 行動呼籲 */}
      <section
        className="py-section-y px-section-x bg-gradient-to-br from-yada-coffee via-yada-brown to-yada-olive text-surface"
        aria-labelledby="pd-cta-title"
      >
        <Container>
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              <h2 id="pd-cta-title" className="text-3xl md:text-4xl font-bold mb-4 text-surface">
                {copy.cta.title}
              </h2>
              <p className="text-surface/90 leading-relaxed mb-8">{copy.cta.text}</p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <a
                  href={SITE.contact.whatsapp.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-accent text-ink px-8 py-4 rounded-yada-full hover:bg-accent-soft transition-all font-semibold focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2"
                >
                  <Icon icon="lucide:message-circle" aria-hidden="true" />
                  <span>{asText(t('products.custom.whatsapp'))}</span>
                </a>
                <a
                  href={buildLocalePath('/contact', locale)}
                  className="inline-flex items-center gap-2 bg-transparent text-surface border-2 border-surface px-8 py-4 rounded-yada-full hover:bg-surface hover:text-primary transition-all font-semibold focus-visible:outline-2 focus-visible:outline-surface focus-visible:outline-offset-2"
                >
                  <span>{asText(t('products.custom.form'))}</span>
                </a>
              </div>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* Back to Products button */}
      <section className="py-section-y px-section-x bg-surface">
        <Container>
          <div className="text-center">
            <Link
              to="/products"
              className="inline-flex items-center gap-2 text-ink-soft hover:text-primary transition-colors font-medium focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
            >
              <Icon icon="lucide:arrow-left" className="text-base" aria-hidden="true" />
              <span>{asText(t('products.backToProducts'))}</span>
            </Link>
          </div>
        </Container>
      </section>
    </>
  )
}
